import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, Mail, MessageCircle, User, MapPin } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { QuoteForm } from "../components/QuoteForm";
import { COMPANY, SERVICES, waLink } from "../lib/constants";

export default function Contact() {
  const [params] = useSearchParams();
  useEffect(() => {
    document.title = "Contact & Free Quote | KAC — Krishnaa Constructions";
  }, []);

  const serviceParam = params.get("service");
  const preselect = SERVICES.find((s) => s.id === serviceParam)?.title || "";

  return (
    <>
      <section data-testid="contact-hero" className="bg-brand-green py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.28em] text-white/70">Contact</p>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">
              Let's talk about your project.
            </h1>
            <p className="mt-5 max-w-2xl leading-relaxed text-white/85">
              Call, WhatsApp or send the form below — whichever suits you. Quotes are free and there's never any obligation.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-4 sm:px-6 lg:grid-cols-5 lg:px-8">
          <Reveal className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-extrabold text-brand-green">Contact details</h2>
            <div className="mt-7 space-y-4">
              <div className="flex items-center gap-4 rounded-2xl border border-brand-light bg-white p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand-green"><User className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-muted">Business</p>
                  <p className="font-semibold text-brand-ink">{COMPANY.name} (KAC) — speak to {COMPANY.contact}</p>
                </div>
              </div>
              <a href={`tel:${COMPANY.phone}`} data-testid="contact-phone" className="flex items-center gap-4 rounded-2xl border border-brand-light bg-white p-5 transition-all hover:border-brand-green/40 hover:shadow-md">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand-green"><Phone className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-muted">Phone</p>
                  <p className="font-semibold text-brand-green">{COMPANY.phoneDisplay}</p>
                </div>
              </a>
              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email" className="flex items-center gap-4 rounded-2xl border border-brand-light bg-white p-5 transition-all hover:border-brand-green/40 hover:shadow-md">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand-green"><Mail className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-muted">Email</p>
                  <p className="font-semibold text-brand-green">{COMPANY.email}</p>
                </div>
              </a>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 rounded-2xl border border-[#25D366]/30 bg-[#25D366]/5 p-5 transition-all hover:shadow-md">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white"><MessageCircle className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-muted">WhatsApp</p>
                  <p className="font-semibold text-brand-green">Message us any time</p>
                </div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-brand-light bg-white p-5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-light text-brand-green"><MapPin className="h-5 w-5" /></span>
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-brand-muted">Service areas</p>
                  <p className="font-semibold text-brand-ink">{COMPANY.areas}, UK</p>
                </div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12} className="lg:col-span-3">
            <div className="rounded-3xl border border-brand-light bg-brand-light/40 p-7 sm:p-10">
              <h2 className="font-heading text-2xl font-extrabold text-brand-green">Request a free quote</h2>
              <p className="mb-8 mt-2 text-sm text-brand-muted">
                Fill in the form and {COMPANY.contact} will get back to you. Fields marked * are required.
              </p>
              <QuoteForm key={preselect} defaultService={preselect} />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
